// Encrypted offline store. Form drafts and other field captures are
// held in IndexedDB so a worker can keep capturing with no signal; everything
// is encrypted at rest and synced later.
//
// Encryption: AES-GCM with a per-(device, worker) key derived via HKDF from a
// 32-byte deviceSecret (generated on first run, cached in IndexedDB). Mixing
// the workerId into the HKDF `info` means a different worker signing in on the
// same device derives a different key and cannot read the first worker's
// records — cryptographic isolation on top of the (device + worker) pin. A
// fresh 12-byte IV per write; the stored value is { iv, ct }.
//
// The deviceSecret is deliberately persistent (not session-scoped): a field
// worker must be able to read their drafts across the offline window without a
// live token. Destroying the secret (wipe) is what makes every
// ciphertext unrecoverable. Records are pinned with (deviceId, workerId,
// expiresAt); the ~7-day TTL + pin sweep harden in .

const DB_NAME = 'asmaluz-shop';
const DB_VERSION = 1;
const STORE_SECRETS = 'secrets';
const STORE_RECORDS = 'sync_queue';

const TTL_MS = 7 * 24 * 60 * 60 * 1000;

// Offline-allowed entity kinds (offline-scope discipline). This union is the
// enforcement point: online-only operations (server-side scoring, cross-record
// lookup, approvals, and reports) are intentionally absent — they cannot be
// stored offline because there is no kind for them.
export type OfflineKind = 'draft' | 'attendance' | 'note' | 'observation';

// Runtime enforcement of the offline-scope discipline, in case an untyped (JS)
// caller bypasses the OfflineKind union: only these kinds may ever be written to
// the device. Online-only operations (server-side scoring, cross-record lookup,
// approvals, and reports) must never be cached offline.
const ALLOWED_KINDS: ReadonlySet<string> = new Set<OfflineKind>([
  'draft',
  'attendance',
  'note',
  'observation',
]);

export interface OfflineEntry<T> {
  id: string;
  payload: T;
}

type Wrapped = { iv: ArrayBuffer; ct: ArrayBuffer };

// PII lives encrypted in `blob`; the rest is opaque ids + timestamps (no PII)
// kept in the clear so the store can pin, expire, list, and sync without
// decrypting. `module`, `clientMutationId`, and `updatedAt` are the sync
// metadata: the owning module (the receiver's authorization axis), a fresh-per-
// write idempotency key, and the last-edit time the server uses as the
// last-write-wins clock.
interface StoredRecord {
  id: string; // `${kind}:${rawId}`
  kind: OfflineKind;
  deviceId: string;
  workerId: string;
  module: string;
  clientMutationId: string;
  updatedAt: number;
  expiresAt: number;
  blob: Wrapped;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const open = indexedDB.open(DB_NAME, DB_VERSION);
    open.onupgradeneeded = () => {
      const db = open.result;
      if (!db.objectStoreNames.contains(STORE_SECRETS)) db.createObjectStore(STORE_SECRETS);
      if (!db.objectStoreNames.contains(STORE_RECORDS)) db.createObjectStore(STORE_RECORDS, { keyPath: 'id' });
    };
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error);
  });
  return dbPromise;
}

async function getOrCreateDeviceSecret(): Promise<ArrayBuffer> {
  const db = await openDB();
  const store = db.transaction(STORE_SECRETS, 'readwrite').objectStore(STORE_SECRETS);
  const existing = await req(store.get('device_secret'));
  if (existing instanceof ArrayBuffer) return existing;
  const fresh = crypto.getRandomValues(new Uint8Array(32)).buffer;
  await req(store.put(fresh, 'device_secret'));
  return fresh;
}

async function deriveKey(workerId: string): Promise<CryptoKey> {
  const secret = await getOrCreateDeviceSecret();
  const ikm = await crypto.subtle.importKey('raw', secret, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new TextEncoder().encode('asmaluz-shop:pii:v1'),
      info: new TextEncoder().encode(`worker:${workerId}`),
    },
    ikm,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

async function wrap(plaintext: string, workerId: string): Promise<Wrapped> {
  const key = await deriveKey(workerId);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(plaintext));
  return { iv: iv.buffer, ct };
}

async function unwrap(w: Wrapped, workerId: string): Promise<string> {
  const key = await deriveKey(workerId);
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: new Uint8Array(w.iv) }, key, w.ct);
  return new TextDecoder().decode(pt);
}

function recordKey(kind: OfflineKind, rawId: string): string {
  return `${kind}:${rawId}`;
}

function isReadable(rec: StoredRecord, workerId: string, did: string): boolean {
  return rec.workerId === workerId && rec.deviceId === did && rec.expiresAt > Date.now();
}

// putOffline encrypts and stores one record, pinned to this device + worker.
// `module` is the owning module the receiver authorizes against
// (e.g. moduleA | moduleB). Each write is a fresh logical mutation: it gets a new
// clientMutationId and bumps updatedAt, so a re-edit before sync supersedes the
// previous queued state and the server resolves it last-write-wins.
export async function putOffline<T>(
  kind: OfflineKind,
  rawId: string,
  workerId: string,
  module: string,
  payload: T
): Promise<void> {
  if (!ALLOWED_KINDS.has(kind)) {
    throw new Error(`offline store: kind "${kind}" is not offline-allowed`);
  }
  const now = Date.now();
  const rec: StoredRecord = {
    id: recordKey(kind, rawId),
    kind,
    deviceId: await deviceId(),
    workerId,
    module,
    clientMutationId: crypto.randomUUID(),
    updatedAt: now,
    expiresAt: now + TTL_MS,
    blob: await wrap(JSON.stringify(payload), workerId),
  };
  const db = await openDB();
  await req(db.transaction(STORE_RECORDS, 'readwrite').objectStore(STORE_RECORDS).put(rec));
}

// getOffline returns the decrypted payload, or null when absent, expired, or
// pinned to a different device/worker.
export async function getOffline<T>(
  kind: OfflineKind,
  rawId: string,
  workerId: string
): Promise<T | null> {
  const db = await openDB();
  const rec = (await req(
    db.transaction(STORE_RECORDS, 'readonly').objectStore(STORE_RECORDS).get(recordKey(kind, rawId))
  )) as StoredRecord | undefined;
  if (!rec || !isReadable(rec, workerId, await deviceId())) return null;
  return JSON.parse(await unwrap(rec.blob, workerId)) as T;
}

// listOffline returns every readable record of a kind for this device + worker,
// decrypted, with the kind prefix stripped from the id.
export async function listOffline<T>(kind: OfflineKind, workerId: string): Promise<Array<OfflineEntry<T>>> {
  const db = await openDB();
  const all = (await req(
    db.transaction(STORE_RECORDS, 'readonly').objectStore(STORE_RECORDS).getAll()
  )) as StoredRecord[];
  const did = await deviceId();
  const out: Array<OfflineEntry<T>> = [];
  for (const rec of all) {
    if (rec.kind !== kind || !isReadable(rec, workerId, did)) continue;
    out.push({ id: rec.id.slice(kind.length + 1), payload: JSON.parse(await unwrap(rec.blob, workerId)) as T });
  }
  return out;
}

// removeOffline drops a record (e.g. once the sync layer has uploaded it).
export async function removeOffline(kind: OfflineKind, rawId: string): Promise<void> {
  const db = await openDB();
  await req(db.transaction(STORE_RECORDS, 'readwrite').objectStore(STORE_RECORDS).delete(recordKey(kind, rawId)));
}

// PendingMutation is one queued record ready to sync — the decrypted payload
// plus the metadata the /api/sync receiver needs to build a mutation.
export interface PendingMutation<T = unknown> {
  kind: OfflineKind;
  rawId: string;
  clientMutationId: string;
  module: string;
  updatedAt: number;
  payload: T;
}

// listPending returns every readable record for this device + worker across all
// kinds, decrypted, with its sync metadata — the source the background-sync
// drain (services/sync.ts) builds its /api/sync batch from. Like listOffline it
// honours the (device, worker) pin and the TTL, so an expired or foreign record
// is never synced.
export async function listPending(workerId: string): Promise<PendingMutation[]> {
  const db = await openDB();
  const all = (await req(
    db.transaction(STORE_RECORDS, 'readonly').objectStore(STORE_RECORDS).getAll()
  )) as StoredRecord[];
  const did = await deviceId();
  const out: PendingMutation[] = [];
  for (const rec of all) {
    if (!isReadable(rec, workerId, did)) continue;
    out.push({
      kind: rec.kind,
      rawId: rec.id.slice(rec.kind.length + 1),
      clientMutationId: rec.clientMutationId,
      module: rec.module,
      updatedAt: rec.updatedAt,
      payload: JSON.parse(await unwrap(rec.blob, workerId)) as unknown,
    });
  }
  return out;
}

// removeIfSynced drops a record only if its clientMutationId still matches the
// one that was synced. A re-edit during the sync round-trip regenerates the id
// (putOffline mints a fresh one), so a compare-and-delete preserves that newer
// pending edit instead of clobbering it — the difference between "this mutation
// landed, clear it" and "clear whatever is here now".
export async function removeIfSynced(
  kind: OfflineKind,
  rawId: string,
  clientMutationId: string
): Promise<void> {
  const db = await openDB();
  const store = db.transaction(STORE_RECORDS, 'readwrite').objectStore(STORE_RECORDS);
  const rec = (await req(store.get(recordKey(kind, rawId)))) as StoredRecord | undefined;
  if (rec && rec.clientMutationId === clientMutationId) {
    await req(store.delete(recordKey(kind, rawId)));
  }
}

// sweepExpired deletes every record past its TTL, actually purging the
// encrypted PII (a read only hides it). Metadata-only — no key needed — so it
// purges expired records belonging to any worker/device on this device, which
// is the right DPA behavior: the ~7-day offline retention is enforced
// regardless of who is signed in. Returns the count purged.
export async function sweepExpired(): Promise<number> {
  const db = await openDB();
  const store = db.transaction(STORE_RECORDS, 'readwrite').objectStore(STORE_RECORDS);
  const all = (await req(store.getAll())) as StoredRecord[];
  const now = Date.now();
  let purged = 0;
  for (const rec of all) {
    if (rec.expiresAt <= now) {
      await req(store.delete(rec.id));
      purged += 1;
    }
  }
  return purged;
}

// Hourly cadence covers a long-lived offline PWA session where records expire
// mid-use; the immediate sweep handles the common boot case.
const SWEEP_INTERVAL_MS = 60 * 60 * 1000;

// startExpirySweep sweeps now, then hourly. Best-effort: a failed sweep must
// never wedge the app. Returns a stop function (the app runs for its lifetime;
// the handle is there for tests and teardown).
export function startExpirySweep(): () => void {
  void sweepExpired().catch(() => undefined);
  const timer = setInterval(() => void sweepExpired().catch(() => undefined), SWEEP_INTERVAL_MS);
  return () => clearInterval(timer);
}

// wipeOfflineData destroys the entire offline database — every encrypted record
// and the device secret that derives their keys (compromised-device
// wipe). Dropping the secret is what makes the wipe irreversible: even if a
// ciphertext were recovered, its key is gone. The service worker deletes the
// same database independently on its `compromised` message; this app-context
// delete is the belt-and-suspenders path when there is no SW controller.
// Best-effort and non-blocking — success, error, and blocked all resolve, since
// the compromise flow must never hang on the wipe.
export async function wipeOfflineData(): Promise<void> {
  if (dbPromise) {
    try {
      (await dbPromise).close();
    } catch {
      // already closing / closed — we're deleting it regardless
    }
    dbPromise = null;
  }
  await new Promise<void>((resolve) => {
    const del = indexedDB.deleteDatabase(DB_NAME);
    del.onsuccess = () => resolve();
    del.onerror = () => resolve();
    del.onblocked = () => resolve();
  });
}

async function deviceId(): Promise<string> {
  const db = await openDB();
  const store = db.transaction(STORE_SECRETS, 'readwrite').objectStore(STORE_SECRETS);
  const existing = (await req(store.get('device_id'))) as string | undefined;
  if (typeof existing === 'string') return existing;
  const fresh = crypto.randomUUID();
  await req(store.put(fresh, 'device_id'));
  return fresh;
}

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}
